/**
 * UserPicker — the one searchable person field (PICK1c).
 *
 * "Zuständig" on a task and "Mitarbeiter" on a work report were the last
 * native ``<select>`` elements in the two forms. They were filled from
 * ``fetchAdminUsers()`` — a call that takes no parameters and returns
 * every user of the organisation — so the form paid for the whole staff
 * list on open, there was no search, and a manager saw an EMPTY field
 * because the underlying route is admin-only and every caller swallowed
 * the 403 into ``setUsers([])``.
 *
 * This is a thin domain wrapper over ``AsyncCombobox``, exactly like
 * ``TaskPicker`` — no second picker mechanism. It owns only the three
 * things that are about people:
 *
 *   - **the label**: name, falling back to the email, never the id;
 *   - **the second line**: email · role, because two colleagues can
 *     share a first name and the role is what tells a manager from a
 *     tenant portal login;
 *   - **hydration**: the stored id is resolved through a route that
 *     also answers for DEACTIVATED users, so a task assigned years ago
 *     still shows its owner instead of looking unassigned — which is
 *     what would make the next save persist an accidental change.
 */
import React from "react";

import {
  fetchAdminUserPickerOptionById,
  fetchAdminUserPickerOptions,
} from "../../../api/adminData";
import AsyncCombobox from "../../../components/ui/AsyncCombobox";

const PAGE_SIZE = 20;

/** German role names, for the second line. Unknown roles fall through to
 *  the raw value rather than disappearing — a silent blank would hide
 *  exactly the case worth seeing. */
const ROLE_LABEL = {
  admin: "Admin",
  manager: "Manager",
  landlord: "Vermieter",
  tenant: "Mieter",
  support: "Support",
  platform_admin: "Platform-Admin",
};

/** Name, or the email when the name is blank. Never the id: a raw UUID
 *  is not a name, and showing one is the failure this picker replaces. */
export function userName(user) {
  if (!user) return "";
  const label = String(user.label || "").trim();
  if (label) return label;
  return String(user.email || "").trim() || "Benutzer ohne Namen";
}

/** ``email · Rolle``, plus ``Inaktiv`` for a historical assignee. */
export function userSecondaryLine(user) {
  if (!user) return "";
  const parts = [];
  const email = String(user.email || "").trim();
  // Suppressed when the email IS the primary line — repeating it twice
  // tells the reader nothing.
  if (email && email !== userName(user)) parts.push(email);
  const role = ROLE_LABEL[user.role] || user.role;
  if (role) parts.push(String(role));
  if (user.is_active === false) parts.push("Inaktiv");
  return parts.join(" · ");
}

export default function UserPicker({
  value,
  onChange,
  label = "Zuständig",
  placeholder = "— niemand zugewiesen —",
  clearLabel = "— niemand zugewiesen —",
  searchPlaceholder = "Person nach Name oder E-Mail suchen …",
  disabled = false,
}) {
  const [selected, setSelected] = React.useState(null);

  // Hydrate the CURRENT selection by id. Without this a person who
  // sorts past page 1 — or who has since been deactivated — renders as
  // "nobody assigned", and saving an otherwise untouched form would
  // write that away.
  React.useEffect(() => {
    if (!value) {
      setSelected(null);
      return undefined;
    }
    if (selected && String(selected.id) === String(value)) return undefined;
    let cancelled = false;
    fetchAdminUserPickerOptionById(value)
      .then((user) => {
        if (!cancelled && user) setSelected(user);
      })
      .catch(() => {
        // An inaccessible user must not break the form: the trigger
        // keeps its placeholder and the stored id stays untouched until
        // the user deliberately changes it.
      });
    return () => {
      cancelled = true;
    };
  }, [value, selected]);

  const fetchPage = React.useCallback(
    ({ query, offset, limit }) =>
      fetchAdminUserPickerOptions({ q: query || undefined, limit, offset }),
    []
  );

  const handleChange = React.useCallback(
    (id, item) => {
      setSelected(item);
      onChange(id);
    },
    [onChange]
  );

  return (
    <AsyncCombobox
      label={label}
      placeholder={placeholder}
      clearLabel={clearLabel}
      searchPlaceholder={searchPlaceholder}
      loadingText="Personen werden geladen …"
      emptyText="Keine passenden Personen gefunden."
      errorText="Personen konnten nicht geladen werden."
      value={value || ""}
      selectedItem={selected}
      onChange={handleChange}
      fetchPage={fetchPage}
      getItemId={(u) => u.id}
      getItemLabel={userName}
      pageSize={PAGE_SIZE}
      disabled={disabled}
      renderItem={(user) => (
        <span className="block min-w-0">
          <span
            title={userName(user)}
            className={
              "block truncate text-[13px] " +
              (user.is_active === false
                ? "font-normal text-muted-foreground"
                : "font-medium text-foreground")
            }
          >
            {userName(user)}
          </span>
          {/* Ellipsizes in a narrow popover — the native tooltip keeps
              the tail (role, status) reachable instead of cutting it. */}
          <span
            title={userSecondaryLine(user)}
            className="block truncate text-[11px] text-muted-foreground"
          >
            {userSecondaryLine(user)}
          </span>
        </span>
      )}
    />
  );
}
